import { useQuery } from '@apollo/client';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Grid, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import type React from 'react';
import { parseSuperView } from 'src/api/viewModels/data';
import { DynamicFieldComponent, type DynamicFieldType } from 'src/components/DynamicField/DyanamicField';
import styles from 'src/components/Tabs/InfoTab/InfoTabStyles';
import { buildSharedSuperView, buildSuperView } from 'src/dynamoQueries/superView';

interface SuperViewField extends DynamicFieldType {
  label: string;
  slug: string;
  fallbackMessage?: string;
}

interface StaticReadViewProps {
  isTwoColumn: boolean;
  id: string;
  viewModel: string;
  highlights?: (string | null)[];
  shared?: boolean;
  compact?: boolean;
}

const StaticReadView: React.FC<StaticReadViewProps> = ({
  isTwoColumn,
  id,
  viewModel,
  highlights,
  shared,
  compact = false,
}) => {
  const classes = styles();

  const {
    data: superView,
    loading,
    error,
  } = useQuery(shared ? buildSharedSuperView() : buildSuperView(), {
    skip: !id || !viewModel,
    variables: { globalId: id, viewComponentName: viewModel },
    fetchPolicy: 'no-cache',
  });

  if (loading) return <CircularProgress color="secondary" />;
  if (error) return <div>`Error! ${error?.message}`</div>;

  const parsedFields = parseSuperView(superView, shared) as SuperViewField[] | undefined;
  const visibleFields = compact
    ? parsedFields?.filter((field, index) => index === 0 || (field.show?.trim() ?? '') !== '')
    : parsedFields;

  return (
    <Grid
      container={isTwoColumn}
      rowSpacing={compact ? 0.75 : 1}
      columnSpacing={compact ? { xs: 1.5, sm: 2, md: 2.5 } : { xs: 1, sm: 2, md: 3 }}
    >
      {visibleFields?.map((field, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: parsed view fields have no stable unique identifier
        <Grid container key={index} item xs={6}>
          <div className={classes.infoBlock}>
            <Typography className={classes.title}>{field.label}:</Typography>
            <Typography
              paragraph={!compact}
              className={classes.value}
              style={compact ? { marginBottom: 0 } : undefined}
            >
              <DynamicFieldComponent field={field} highlights={highlights} />
              {field.fallbackMessage && (
                <Tooltip title={field.fallbackMessage} arrow>
                  <WarningAmberIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, verticalAlign: 'middle' }} />
                </Tooltip>
              )}
            </Typography>
          </div>
        </Grid>
      ))}
    </Grid>
  );
};

export default StaticReadView;
